import {assetsRequestRoutine} from '~/redux/assets/assetsActions';
import {selectAssetsResource} from '~/redux/assets/assetsResource';

import {Decoder} from '@raison/prelude';
import {AccountId, BaseCurrencyCode, Resource} from '@raison/types';
import BigNumber from 'bignumber.js';
import React from 'react';
import {MAX_DECIMALS_IN_CRYPTO} from '~/config';
import {CryptoCurrencyTrait, CryptoCurrencyType, useCurrenciesInfoPrefetch} from '~/entities/currencies';
import {FallbackPage, NotFoundPage} from '~/entities/fallbacks';
import {BlockchainInfo} from '~/entities/operations-crypto';
import {OperationSelectButton} from '~/entities/operations-fiat';
import {RootLayout, RsnInput} from '~/library/ui-kit';
import {selectFinancesResource} from '~/redux/finances';
import {OperationAlias, OperationModel} from '~/redux/finances/domain';
import {operationsRequestRoutine} from '~/redux/finances/financesEffects';
import {getAllOperations} from '~/redux/finances/financesPrimitives';
import {createOperationRoute} from '~/redux/finances/financesRouting';
import {Assets} from '~/shared/api/legacy_services/Assets/Assets';
import {bindTracedSelector, useAppDispatch} from '~/shared/store';
import {SendTxDirector} from '../ui/SendTxDirector';
import {UserInfoResource, useUserInfoResource} from './useUserInfoResource';

type Props = {
    accountId: string;
    accountType: CryptoCurrencyType;
};

const AccountIdFromString = Decoder.prepare(Number)(AccountId);

export function SendTxPageContainer({accountId: accountIdString, accountType}: Props) {
    const accountId = Decoder.run(AccountIdFromString, accountIdString).orThrow();
    const assetR = useAssetAccountResource(accountId, accountType);
    const userR = useUserInfoResource();
    useCurrenciesInfoPrefetch();

    const dispatch = useAppDispatch();

    const updateResources = () => {
        // userR.refetch() // no need
        dispatch(assetsRequestRoutine.trigger());
        dispatch(operationsRequestRoutine.trigger());
    };

    if (assetR.isError || userR.isError) {
        return <FallbackPage />;
    }
    if (!assetR.isSuccess || !userR.isSuccess) {
        return <SkeletonPage />;
    }
    if (!assetR.data) {
        return <NotFoundPage />;
    }

    const resources = {...assetR.data, ...userR.data};
    return <SendTxDirector resources={resources} updateResources={updateResources} />;
}

function SkeletonPage() {
    return (
        <RootLayout common title='Send Crypto'>
            <OperationSelectButton.Skeleton />
            <RsnInput.Skeleton />
            <RsnInput.Skeleton />
        </RootLayout>
    );
}

export interface SendTxLimits {
    /**
     * Either balance or maximum limit on transaction
     */
    upper: BigNumber;
    /**
     * Minimal allowed amount to send
     */
    lower: BigNumber;
    /**
     * Upper limit on how much fee user can pay
     */
    fee: BigNumber;
}

export interface SendTxResources {
    user: UserInfoResource;

    amountLimit: SendTxLimits;

    asset: {
        id: AccountId;
        type: CryptoCurrencyType;
        icon: string | null;
        name: string;
        code: BaseCurrencyCode;
        decimals: number;
        blockchain: BlockchainAccount;
        locked: BigNumber;
    };

    routes: {
        buy: string | null;
    };
}

interface BlockchainAccount extends BlockchainInfo, CryptoCurrencyTrait {
    icon: string | null;
    name: string;
    address: string;
}

export const useAssetAccountResource = bindTracedSelector(
    (state: AppModel, accountId: AccountId, accountType: CryptoCurrencyType) =>
        Resource.all(
            selectAssetsResource(state),
            selectFinancesResource(state).mapSuccess(getAllOperations)
        ).mapSuccess(([assets, finances]) => selectSendTxResources(assets, finances, accountId, accountType))
);

function selectSendTxResources(
    {accounts, minimum}: Pick<Assets, 'accounts' | 'minimum'>,
    operations: OperationModel[],
    accountId: AccountId,
    accountType: CryptoCurrencyType
): null | Omit<SendTxResources, 'user'> {
    const account = (() => {
        switch (accountType) {
            case 'crypto': {
                const account = accounts.crypto.find(({id}) => id === accountId);
                if (account) return {...account, type: accountType};
                break;
            }
            case 'stablecoin': {
                const account = accounts.tokens.find(({id}) => id === accountId);
                if (account) return {...account, type: accountType};
                break;
            }
        }
    })();
    if (!account) {
        return null;
    }
    const blockchainAccount = accounts.crypto.find(({currency}) => currency === account.blockchain.currency);

    if (!blockchainAccount) {
        return null;
    }

    const temporaryBlocked = BigNumber(blockchainAccount.balance_locked);

    const minimalAmount = minimum.send[account.currency] ?? 0;
    // Decision was made to just allow amounts greater then zero
    const lowerLimit = BigNumber(minimalAmount);
    // const dustAmount = 10 ** -account.decimals;
    // const lowerLimit = BigNumber.max(minimalAmount, dustAmount);

    const decimals = Math.min(account.decimals, MAX_DECIMALS_IN_CRYPTO);

    // truncating balance because Use All cannot work correctly if precisions are different
    const upperLimit = new BigNumber(account.balance)
        .multipliedBy(10 ** decimals)
        .integerValue(BigNumber.ROUND_DOWN)
        .dividedBy(10 ** decimals);

    const feeAssetBuyOperation =
        operations.find(o => OperationAlias.from(o) === 'buy' && o.target.id === blockchainAccount.id) ?? null;

    return {
        amountLimit: {
            lower: lowerLimit,
            upper: upperLimit,
            fee: BigNumber(blockchainAccount.balance),
        },
        asset: {
            id: account.id,
            type: account.type,
            icon: account.icon,
            name: account.name,
            code: account.currency,
            decimals,
            blockchain: {
                networkName: account.blockchain.network_name,
                currencyCode: blockchainAccount.currency,
                currencyDecimals: Math.min(blockchainAccount.decimals, MAX_DECIMALS_IN_CRYPTO),
                name: blockchainAccount.name,
                icon: blockchainAccount.icon,
                address: blockchainAccount.address,
            },
            locked: temporaryBlocked,
        },
        routes: {
            buy: feeAssetBuyOperation && createOperationRoute(feeAssetBuyOperation),
        },
    };
}
